/* eslint-disable react/jsx-key */

import { useEffect, useMemo, useState } from 'react';

import { SortByAlpha, ArrowDropUp, ArrowDropDown } from '@mui/icons-material';
import {
  Pagination,
  Select,
  MenuItem,
  LinearProgress,
  Checkbox,
  Button,
} from '@mui/material';
import {
  useTable,
  Column,
  useResizeColumns,
  useFlexLayout,
  useSortBy,
  usePagination,
  useFilters,
  TableOptions,
} from 'react-table';

import { Spinner } from 'components/Spinner';
import { useUrlPagination } from 'hooks';

import {
  TableWrapper,
  MessageRow,
  ControlWrapper,
  CheckboxWrapper,
} from './styleComponents';
import { FilterBasic } from './TableFilters/FilterBasic';
export type TableControl = {
  key: string;
  text: string;
  icon: JSX.Element;
  disabled: boolean;
  click: () => void;
};
export type TableControls = {
  ml: number;
  selectAll: (props: { action: boolean; entities: Array<any> }) => void;
  controls: Array<TableControl>;
  selected: Set<number>;
};

export type TableProps<
  T extends Record<string, unknown> = Record<string, unknown>,
> = {
  addButton?: JSX.Element;
  useTableOptions: TableOptions<T>;
  loading: boolean;
  showPagination?: boolean;
  pageSizeOptions?: Array<number>;
  className?: string;
  globalFiltersApplied?: boolean;
  handleGlobalFilterClear?: () => void;
  count?: number;
  tableControls?: TableControls;
  counterClick?: () => void;
  rowProps?: (row: any) => Record<string, unknown>;
};

export const Table = <
  T extends Record<string, unknown> = Record<string, unknown>,
>(
  props: TableProps<T>,
) => {
  const {
    loading,
    useTableOptions,
    showPagination = true,
    pageSizeOptions = [10, 20, 30, 40, 50, 100, 150],
    className,
    globalFiltersApplied,
    handleGlobalFilterClear,
    count,
    tableControls,
    counterClick,
    rowProps,
  } = props;

  const {
    controlledPageIndex,
    controlledRows,
    updateControlledPageIndex,
    updateControlledRows,
  } = useUrlPagination();
  const defaultColumn: Partial<Column<T>> = useMemo(
    () => ({
      // Default Filter UI
      Filter: FilterBasic,
    }),
    [],
  );
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    page,
    pageCount,
    setAllFilters,
    gotoPage,
    setPageSize,
    state: { pageSize, pageIndex, filters },
  } = useTable(
    {
      defaultColumn,
      ...useTableOptions,
    },

    useFlexLayout,
    useResizeColumns,
    useFilters,
    useSortBy,
    usePagination,
  );

  const [selectAll, setSelectAll] = useState(false);
  const [disableSelectAll, setDisableSelectAll] = useState(false);

  // used to ensure selectAll reflects the current page
  useEffect(() => {
    const active = page.filter(
      ({ original: { tranche_id } }) => !Number.isInteger(tranche_id),
    );
    setDisableSelectAll(!active.length);
    setSelectAll(
      !active
        .map(({ original: { id } }) => id)
        .filter((id) => !tableControls?.selected.has(id as number)).length,
    );
  }, [page, tableControls]);

  useEffect(() => {
    if (pageIndex !== controlledPageIndex - 1) {
      gotoPage(controlledPageIndex - 1);
    }
    if (pageSize !== controlledRows) {
      setPageSize(controlledRows);
    }
  }, [
    pageSize,
    controlledPageIndex,
    controlledRows,
    pageIndex,
    gotoPage,
    setPageSize,
  ]);

  const selected = useMemo(
    () => tableControls?.selected.size ?? 0,
    [tableControls],
  );

  return (
    <TableWrapper className={className}>
      <div className='table' {...getTableProps()}>
        <div className='thead'>
          {headerGroups.map((headerGroup) => (
            <div className='tr' {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column) => (
                <div
                  role='columnheader'
                  {...column.getHeaderProps(
                    column.getSortByToggleProps({
                      className: `th ${column.classNameHeader ?? ''}`,
                    }),
                  )}
                >
                  <div className='th wrapper'>
                    {column.render('Header')}
                    {column.canSort &&
                      (column.isSorted ? (
                        column.isSortedDesc ? (
                          <ArrowDropDown />
                        ) : (
                          <ArrowDropUp />
                        )
                      ) : (
                        <SortByAlpha />
                      ))}
                    {column.canResize && (
                      <div
                        {...column.getResizerProps()}
                        className={`resizer ${
                          column.isResizing ? 'isResizing' : ''
                        }`}
                      />
                    )}
                  </div>
                </div>
              ))}
            </div>
          ))}
          {headerGroups.map((headerGroup) => (
            <div
              className='tr filter'
              aria-hidden
              {...headerGroup.getHeaderGroupProps()}
            >
              {headerGroup.headers.map((column) => (
                <div
                  {...column.getHeaderProps({
                    className: `th filter${column.classNameHeader ?? ''}`,
                  })}
                >
                  {/* TODO: Enable the abiltiy to remove filtering */}
                  {column.canFilter ? column.render('Filter') : null}
                </div>
              ))}
            </div>
          ))}
          {loading && <LinearProgress sx={{ width: '100%' }} />}
          {(globalFiltersApplied || filters.length > 0) && (
            <MessageRow>
              Filters Are Currently Applied. &nbsp;
              <u
                onClick={() => {
                  setAllFilters([]);
                  if (handleGlobalFilterClear) {
                    handleGlobalFilterClear();
                  }
                }}
              >
                Clear Filters
              </u>
            </MessageRow>
          )}

          {tableControls && (
            <ControlWrapper ml={tableControls.ml}>
              <td>
                <CheckboxWrapper>
                  <Checkbox
                    key='select all'
                    role='checkbox'
                    inputProps={{
                      'aria-label': 'select all records',
                    }}
                    aria-checked
                    // aria-disabled
                    checked={!disableSelectAll && selectAll}
                    disabled={disableSelectAll}
                    size='small'
                    onChange={() => {
                      tableControls.selectAll({
                        entities: page.map(({ original }) => original),
                        action: !selectAll,
                      });
                      setSelectAll((prev) => !prev);
                    }}
                  />
                </CheckboxWrapper>{' '}
              </td>
              {tableControls.controls.map((control) => (
                <td>
                  <Button
                    key={control.key}
                    disabled={control.disabled}
                    aria-disabled
                    onClick={control.click}
                    variant='text'
                    startIcon={control.icon}
                  >
                    {control.text}
                  </Button>
                </td>
              ))}
              <td>
                <Button
                  className='select-counter'
                  disabled={!selected}
                  aria-disabled
                  variant='contained'
                  size='small'
                  startIcon={<span>{selected}</span>}
                  onClick={counterClick}
                >
                  Candidate{selected === 1 ? null : 's'} Selected
                </Button>
              </td>
            </ControlWrapper>
          )}
        </div>
        {loading ? (
          <Spinner />
        ) : (
          <div className='tbody' {...getTableBodyProps()}>
            {page.map((row) => {
              prepareRow(row);
              return (
                <div
                  aria-label={'Select ' + row.original.id}
                  className={
                    tableControls?.selected.has(row.original.id as number)
                      ? 'tr checked'
                      : 'tr'
                  }
                  {...row.getRowProps(rowProps ? rowProps(row.original) : {})}
                >
                  {row.cells.map((cell) => {
                    return (
                      <div
                        {...cell.getCellProps({
                          className: `td ${cell.column.className}`,
                        })}
                      >
                        {cell.render('Cell')}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {showPagination && (
        <div className='pagination'>
          <div>
            <span>
              {pageIndex * pageSize}-
              {(pageIndex + 1) * pageSize > (count ? count : rows.length)
                ? rows.length
                : (pageIndex + 1) * pageSize}{' '}
              from {count ? count : rows.length}
            </span>

            <Pagination
              count={pageCount}
              page={controlledPageIndex}
              onChange={(_, page_number) => {
                updateControlledPageIndex(page_number);
              }}
            />

            <div className='pageSelect'>
              <span>Rows per page</span>
              <Select
                disabled={rows.length < pageSize}
                value={controlledRows}
                variant='standard'
                onChange={({ target: { value } }) =>
                  updateControlledRows(Number(value))
                }
              >
                {pageSizeOptions.map((pageSizeOption) => (
                  <MenuItem key={pageSizeOption} value={pageSizeOption}>
                    {pageSizeOption}
                  </MenuItem>
                ))}
              </Select>
            </div>
          </div>
        </div>
      )}
    </TableWrapper>
  );
};
